"use client";

import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { useNewsletterSubscribe } from "./use-newsletter-subscribe";
import { useSubscriptionStatus } from "./use-subscription-status";

export function MobileFooterNewsletter() {
  const {
    email,
    setEmail,
    handleSubmit,
    isSubmitting,
    isError,
    state,
    subscribeToEdition,
  } = useNewsletterSubscribe({ source: "mobile_footer" });

  const { subscribedToCurrent, subscribedToOther, otherEdition, otherLabel } =
    useSubscriptionStatus();

  const [upsellSubmitting, setUpsellSubmitting] = useState(false);
  const [upsellDone, setUpsellDone] = useState(false);

  const justSubscribed = state.status === "success";

  // Already subscribed (returning user) — hide entirely
  if (subscribedToCurrent && !justSubscribed) {
    return null;
  }

  const handleUpsell = async () => {
    setUpsellSubmitting(true);
    const ok = await subscribeToEdition({
      edition: otherEdition,
      includeEditions: ["engineer", "creative"],
    });
    setUpsellSubmitting(false);
    if (ok) {
      setUpsellDone(true);
    }
  };

  // After upsell done — brief confirmation then nothing on next visit
  if (upsellDone) {
    return (
      <div className="flex items-center justify-center gap-1.5 lg:hidden">
        <CheckCircle2 className="h-5 w-5 text-[var(--explore-success-text)]" />
        <span className="font-semibold text-[0.85rem] text-[var(--explore-success-text)]">
          You're getting both digests!
        </span>
      </div>
    );
  }

  // Upsell after subscribing
  if (justSubscribed && !subscribedToOther) {
    return (
      <div className="flex w-full max-w-[400px] flex-col items-center gap-2 lg:hidden">
        <div className="flex items-center gap-1.5">
          <CheckCircle2 className="h-4 w-4 text-[var(--explore-success-text)]" />
          <span className="font-medium text-[0.85rem] text-[var(--explore-success-text)]">
            Subscribed!
          </span>
        </div>
        <p className="text-[0.8rem] text-[var(--explore-text-muted)]">
          Want the {otherLabel} digest too?
        </p>
        <button
          className="rounded-full bg-[var(--explore-accent-yellow)] px-6 py-2.5 font-semibold text-[0.85rem] text-[var(--explore-on-accent-text)] transition-transform duration-150 active:scale-95"
          onClick={handleUpsell}
          type="button"
        >
          {upsellSubmitting ? "Adding..." : `Yes, add ${otherLabel} too`}
        </button>
      </div>
    );
  }

  // Already done (subscribed to both or just subscribed to other already)
  if (justSubscribed) {
    return null;
  }

  // Default: email form
  return (
    <form
      className="flex w-full max-w-[400px] flex-col gap-2 lg:hidden"
      onSubmit={handleSubmit}
    >
      <input
        className="w-full rounded-full border border-[var(--explore-border)] bg-[var(--color-pure-white)] px-4 py-2.5 text-center text-[0.85rem] outline-none focus:border-[var(--explore-accent-secondary)]"
        disabled={isSubmitting}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        required
        type="email"
        value={email}
      />
      {isError && (
        <p className="text-center text-[0.75rem] text-[var(--explore-error-text)]">
          {state.message}
        </p>
      )}
      <button
        className="rounded-full bg-[var(--explore-accent-yellow)] px-6 py-2.5 font-semibold text-[0.85rem] text-[var(--explore-on-accent-text)] transition-transform duration-150 active:scale-95"
        type="submit"
      >
        {isSubmitting ? "Subscribing..." : "Subscribe to newsletter"}
      </button>
    </form>
  );
}
